import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { productApi, contactApi, Product, getImageUrl } from '../services/api';
import './Home.css';

interface CategoryInfo {
  name: string;
  description: string;
  imageUrl: string;
}

const Home: React.FC = () => {
  const [, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [inquiryModalVisible, setInquiryModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [inquiryForm, setInquiryForm] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    estimatedQuantity: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const successModalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // 分类配置：图片和描述
  const categoryConfig: Record<string, { description: string; imageUrl: string }> = {
    'Amenities Sets': {
      description: 'All-in-one guest room amenity kits',
      imageUrl: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&q=80'
    },
    'Slippers': {
      description: 'Comfortable one-time-use slippers',
      imageUrl: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=400&q=80'
    },
    'Soap': {
      description: 'Bar soaps and liquid soap options',
      imageUrl: 'https://images.unsplash.com/photo-1600857544200-b2f666a9a2ec?w=400&q=80'
    },
    'Tubes & Bottles': {
      description: 'Shampoo, conditioner, and body wash',
      imageUrl: 'https://images.unsplash.com/photo-1631125915902-d8abe9225ff2?w=400&q=80'
    },
    'Linens': {
      description: 'Towels and bed linens for guests',
      imageUrl: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&q=80'
    },
    'Dining': {
      description: 'Tableware and dining essentials',
      imageUrl: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&q=80'
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, featuredRes] = await Promise.all([
          productApi.getAll(true),
          productApi.getFeatured() // 获取Product Management中Featured设置为yes的产品
        ]);
        setProducts(productsRes.data.slice(0, 8)); // Show 8 products for other sections
        setFeaturedProducts(featuredRes.data); // Featured products for third section - 只包含isFeatured=true且isActive=true的产品
        
        // 提取所有唯一的分类
        const categorySet = new Set<string>();
        productsRes.data.forEach(product => {
          if (product.category && product.category.trim()) {
            categorySet.add(product.category.trim());
          }
        });

        // 构建分类信息数组
        const categoryList: CategoryInfo[] = Array.from(categorySet).map(category => {
          const config = categoryConfig[category] || {
            description: `Quality ${category.toLowerCase()} for hotels`,
            imageUrl: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400&q=80'
          };
          return {
            name: category,
            description: config.description,
            imageUrl: config.imageUrl
          };
        });

        // 如果没有分类，使用默认分类
        if (categoryList.length === 0) {
          setCategories([
            { name: 'Amenities Sets', description: 'All-in-one guest room amenity kits', imageUrl: categoryConfig['Amenities Sets'].imageUrl },
            { name: 'Slippers', description: 'Comfortable one-time-use slippers', imageUrl: categoryConfig['Slippers'].imageUrl },
            { name: 'Soap', description: 'Bar soaps and liquid soap options', imageUrl: categoryConfig['Soap'].imageUrl },
            { name: 'Tubes & Bottles', description: 'Shampoo, conditioner, and body wash', imageUrl: categoryConfig['Tubes & Bottles'].imageUrl }
          ]);
        } else {
          setCategories(categoryList);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        // 使用默认分类
        setCategories([
          { name: 'Amenities Sets', description: 'All-in-one guest room amenity kits', imageUrl: categoryConfig['Amenities Sets'].imageUrl },
          { name: 'Slippers', description: 'Comfortable one-time-use slippers', imageUrl: categoryConfig['Slippers'].imageUrl },
          { name: 'Soap', description: 'Bar soaps and liquid soap options', imageUrl: categoryConfig['Soap'].imageUrl },
          { name: 'Tubes & Bottles', description: 'Shampoo, conditioner, and body wash', imageUrl: categoryConfig['Tubes & Bottles'].imageUrl }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理成功弹窗自动关闭（3秒后）
  useEffect(() => {
    if (successModalVisible) {
      // 清除之前的定时器（如果有）
      if (successModalTimeoutRef.current) {
        clearTimeout(successModalTimeoutRef.current);
      }
      // 设置3秒后自动关闭
      successModalTimeoutRef.current = setTimeout(() => {
        setSuccessModalVisible(false);
      }, 3000);
    }

    // 清理函数：组件卸载或弹窗关闭时清除定时器
    return () => {
      if (successModalTimeoutRef.current) {
        clearTimeout(successModalTimeoutRef.current);
      }
    };
  }, [successModalVisible]);

  // 关闭成功弹窗的通用函数
  const closeSuccessModal = () => {
    // 清除自动关闭定时器
    if (successModalTimeoutRef.current) {
      clearTimeout(successModalTimeoutRef.current);
    }
    setSuccessModalVisible(false);
  };

  return (
    <div className="home">
      {/* Hero Section - Left Text, Right Image */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content-wrapper">
            {/* Left Content */}
            <div className="hero-text">
              <div className="hero-line"></div>
              <p className="hero-label">UPWORK HOTEL SUPPLIERS</p>
              <h1 className="hero-title">
                Premium Hotel<br />
                Amenities Supplier<br />
                Malaysia
              </h1>
              <p className="hero-description">
                We help hotels and wellness venues across Malaysia deliver better stays 
                with a selected range of toiletries, linens, and disposables—all made to high standards.
              </p>
              <div className="hero-buttons">
                <Link to="/products" className="btn btn-primary">
                  View Products <span className="arrow">→</span>
                </Link>
                <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
              </div>
            </div>
            {/* Right Image */}
            <div className="hero-image-wrapper">
              <div className="hero-image">
                <img 
                  src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80" 
                  alt="Modern Hotel Bathroom" 
                />
                <div className="hero-image-overlay">
                  <p className="overlay-title">Your Reliable</p>
                  <p className="overlay-subtitle">Hospitality Partner</p>
                  <p className="overlay-location">Malaysia</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="categories-section">
        <div className="container">
          <div className="categories-header">
            <h2 className="categories-title">Hotel Supplies & Amenities by Category</h2>
            <p className="categories-subtitle">
              Competitive pricing, dependable quality, and quick delivery throughout Malaysia.
            </p>
          </div>
          <div className="categories-grid">
            {categories.map((category) => (
              <Link
                key={category.name}
                to={`/products?category=${encodeURIComponent(category.name)}`}
                className="category-card"
              >
                <div className="category-image">
                  <img src={category.imageUrl} alt={category.name} />
                </div>
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Hotel Amenities Section */}
      <section className="featured-amenities-section">
        <div className="container">
          <div className="featured-header">
            <h2 className="featured-title">Featured Hotel Amenities</h2>
            <p className="featured-subtitle">
              Browse our best-selling amenities, chosen by top hotels and resorts across Malaysia.
            </p>
          </div>
          {loading ? (
            <div className="loading">Loading products...</div>
          ) : featuredProducts.length > 0 ? (
            <div className="featured-grid">
              {/* 只显示在Product Management中Featured设置为yes的产品 */}
              {featuredProducts.slice(0, 4).map((product, index) => {
                // 判断是否为新产品（可以根据创建时间或索引判断）
                const isNew = index === 2; // 第三个产品显示NEW标签，可以根据实际需求调整
                return (
                  <div key={product.id} className="featured-card">
                    <div className="featured-image-wrapper">
                      {isNew && <span className="new-badge">NEW</span>}
                      <div className="featured-image">
                        {product.imageUrl ? (
                          <img src={getImageUrl(product.imageUrl)} alt={product.name} />
                        ) : (
                          <img 
                            src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&q=80" 
                            alt={product.name} 
                          />
                        )}
                      </div>
                    </div>
                    <div className="featured-info">
                      <h3>{product.name}</h3>
                      <p>{product.description?.substring(0, 80)}...</p>
                      <button 
                        className="btn-inquire"
                        onClick={() => {
                          setSelectedProduct(product);
                          setInquiryModalVisible(true);
                        }}
                      >
        Inquire Now
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-products">
              <p>No featured products available right now.</p>
            </div>
          )}
          <div className="featured-footer">
            <Link to="/products" className="btn-view-all-products">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <div className="container">
          <div className="statistics-grid">
            <div className="stat-item">
              <div className="stat-number">3+</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50+</div>
              <div className="stat-label">Happy Clients</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">200+</div>
              <div className="stat-label">Products Delivered</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">100<span className="stat-percent">%</span></div>
              <div className="stat-label">Quality Assured</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="why-choose-content">
            <div className="why-choose-text">
              <h2 className="why-choose-title">
                Why Choose <span className="highlight">UPWORK</span> Hotel Suppliers?
              </h2>
              <p className="why-choose-intro">
                As a trusted supplier for Malaysia’s hotels and wellness sector, we focus on 
                reliable, consistent, and value-for-money solutions that improve the guest experience.
              </p>
              <div className="why-choose-features">
                <div className="why-choose-feature-item">
                  <div className="feature-icon-box">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 5L25 12L33 14L28 20L29 28L20 25L11 28L12 20L7 14L15 12L20 5Z" fill="#D4A574"/>
                      <path d="M20 10L23.5 15L30 16.5L26 21L26.5 28L20 25L13.5 28L14 21L10 16.5L16.5 15L20 10Z" fill="#F5E6D3"/>
                    </svg>
                  </div>
                  <h3>Quality Assured</h3>
                  <p>Rigorous quality checks on every item so your guests always receive products that meet high standards.</p>
                </div>
                <div className="why-choose-feature-item">
                  <div className="feature-icon-box">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="15" width="30" height="20" rx="2" fill="#D4A574"/>
                      <rect x="8" y="18" width="24" height="14" rx="1" fill="#F5E6D3"/>
                      <circle cx="12" cy="25" r="2" fill="#D4A574"/>
                      <path d="M20 20L28 25L20 30V20Z" fill="#D4A574"/>
                    </svg>
                  </div>
                  <h3>Fast Delivery</h3>
                  <p>Our Malaysia-wide logistics get your orders to your property on time.</p>
                </div>
                <div className="why-choose-feature-item">
                  <div className="feature-icon-box">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="15" fill="#D4A574"/>
                      <circle cx="20" cy="20" r="10" fill="#F5E6D3"/>
                      <path d="M20 10V20L26 26" stroke="#D4A574" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3>Competitive Pricing</h3>
                  <p>Volume discounts and flexible options to fit your budget.</p>
                </div>
                <div className="why-choose-feature-item">
                  <div className="feature-icon-box">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 8C15 8 11 12 11 17C11 22 15 26 20 26C25 26 29 22 29 17C29 12 25 8 20 8Z" fill="#D4A574"/>
                      <path d="M20 12C16.5 12 13.5 15 13.5 18.5C13.5 22 16.5 25 20 25C23.5 25 26.5 22 26.5 18.5C26.5 15 23.5 12 20 12Z" fill="#F5E6D3"/>
                      <path d="M20 15L22 19L26 20L23 23L23 28L20 26L17 28L17 23L14 20L18 19L20 15Z" fill="#D4A574"/>
                    </svg>
                  </div>
                  <h3>Dedicated Support</h3>
                  <p>A dedicated team on hand to help with all your amenity and supply needs.</p>
                </div>
              </div>
            </div>
            <div className="why-choose-image">
              <img 
                src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80" 
                alt="Luxury Hotel Resort" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="commitment-section">
        <div className="container">
          <div className="commitment-header">
            <h2 className="commitment-title">Our Commitment to Malaysia Hotels</h2>
            <div className="commitment-underline"></div>
            <p className="commitment-description">
              UPWORK is here to back Malaysia’s hospitality sector with dependable products and outstanding service.
            </p>
          </div>
          <div className="commitment-cards">
            <div className="commitment-card">
              <div className="commitment-icon-circle">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4L20 8L24 6L22 12L24 18L16 16L8 18L10 12L8 6L12 8L16 4Z" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M16 8L18 12L20 14L18 16L16 18L14 16L12 14L14 12L16 8Z" stroke="#8B5A3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <h3>Quality First</h3>
              <p>Each product is quality-checked to align with international benchmarks.</p>
            </div>
            <div className="commitment-card">
              <div className="commitment-icon-circle">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 6C11 6 7 10 7 15C7 20 11 24 16 24C21 24 25 20 25 15C25 10 21 6 16 6Z" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M16 10L18 14L22 16L18 18L16 22L14 18L10 16L14 14L16 10Z" stroke="#8B5A3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <h3>Sustainable Choices</h3>
              <p>Eco-friendly options for properties that care about the environment.</p>
            </div>
            <div className="commitment-card">
              <div className="commitment-icon-circle">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="4" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <circle cx="20" cy="12" r="4" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M8 20C8 18 9 16 12 16C15 16 16 18 16 20" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <path d="M16 20C16 18 17 16 20 16C23 16 24 18 24 20" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <h3>Client-Focused</h3>
              <p>Service and support shaped around your property’s needs.</p>
            </div>
            <div className="commitment-card">
              <div className="commitment-icon-circle">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 4L18 12L26 14L20 18L22 26L16 22L10 26L12 18L6 14L14 12L16 4Z" stroke="#8B5A3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </div>
              <h3>Consistent Excellence</h3>
              <p>Steady supply and on-time delivery so your operations stay on track.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stay Updated Section */}
      <section className="stay-updated-section">
        <div className="container">
          <div className="stay-updated-content">
            <div className="stay-updated-form-card">
              <div className="stay-updated-header">
                <div className="stay-updated-icon-circle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="L22 6L12 13L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2>Stay Updated</h2>
              </div>
              <p className="stay-updated-description">
                Sign up for news on new products, industry tips, and exclusive offers for hotel and hospitality teams.
              </p>
              <form 
                className="stay-updated-form"
                method="POST"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!email || emailSubmitting) return;
                  
                  setEmailSubmitting(true);
                  try {
                    // 调用API保存订阅
                    await contactApi.subscribe(email);
                    setEmailSuccess(true);
                    setEmail('');
                    setTimeout(() => setEmailSuccess(false), 3000);
                  } catch (error) {
                    console.error('Error subscribing:', error);
                    alert('订阅失败，请稍后重试');
                  } finally {
                    setEmailSubmitting(false);
                  }
                }}
              >
                {!emailSuccess ? (
                  <div className="stay-updated-input-group">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={emailSubmitting}
                    />
                    <button 
                      type="submit" 
                      className="stay-updated-submit-btn"
                      disabled={emailSubmitting || !email}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="stay-updated-success-box">
                    <p className="stay-updated-success-text">Thank you for subscribing!</p>
                  </div>
                )}
              </form>
              <p className="stay-updated-privacy">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </div>
            <div className="stay-updated-image">
              <img 
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80" 
                alt="Business Meeting" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="contact-info-section">
        <div className="container">
          <div className="contact-info-content">
            <div className="contact-info-left">
              <h2 className="contact-info-title">
                <span className="contact-info-title-line1">Questions?</span>
                <span className="contact-info-title-line2">Get in Touch With Us</span>
              </h2>
              <p className="contact-info-description">
                Want to upgrade your guest experience? We’re here to help you choose the right amenities for your property.
              </p>
              <div className="contact-info-items">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2C7.2 2 5 4.2 5 7C5 11.25 10 18 10 18C10 18 15 11.25 15 7C15 4.2 12.8 2 10 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="10" cy="7" r="2.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span>No. 14, Jalan Utama 2/20, Taman Perindustrian Puchong Utama, 47100 Puchong, Selangor D.E.</span>
                </div>
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 14V16C18 16.5304 17.7893 17.0391 17.4142 17.4142C17.0391 17.7893 16.5304 18 16 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18 6L12 11L8 7L2 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span>+6011-56633168</span>
                </div>
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 4L10 11L18 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span>sales.upworkhotel@gmail.com</span>
                </div>
              </div>
            </div>
            <div className="contact-info-right">
              <Link to="/contact" className="contact-info-btn">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Modal */}
      {inquiryModalVisible && (
        <div className="inquiry-modal-overlay" onClick={() => setInquiryModalVisible(false)}>
          <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inquiry-modal-header">
              <div>
                <h2>Send Inquiry</h2>
                {selectedProduct && (
                  <p className="inquiry-product-name">Product: {selectedProduct.name}</p>
                )}
              </div>
              <button 
                className="inquiry-modal-close"
                onClick={() => setInquiryModalVisible(false)}
              >
                ×
              </button>
            </div>
            <form 
              className="inquiry-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                try {
                  const message = `Product: ${selectedProduct?.name || 'N/A'}\n` +
                    `Estimated Quantity: ${inquiryForm.estimatedQuantity || 'N/A'}\n\n` +
                    inquiryForm.message;
                  
                  await contactApi.create({
                    name: inquiryForm.name,
                    email: inquiryForm.email,
                    phone: inquiryForm.phone || undefined,
                    message: message,
                    productId: selectedProduct?.id
                  });
                  
                  // 关闭表单弹窗，显示成功弹窗
                  setInquiryModalVisible(false);
                  setSuccessModalVisible(true);
                  setInquiryForm({
                    companyName: '',
                    name: '',
                    email: '',
                    phone: '',
                    estimatedQuantity: '',
                    message: ''
                  });
                } catch (error) {
                  alert('Failed to submit inquiry. Please try again.');
                  console.error('Error submitting inquiry:', error);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <div className="inquiry-form-row">
                <div className="inquiry-form-group">
                  <label htmlFor="companyName">Company Name</label>
                  <input
                    type="text"
                    id="companyName"
                    value={inquiryForm.companyName}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, companyName: e.target.value })}
                    placeholder="Your company"
                  />
                </div>
                <div className="inquiry-form-group">
                  <label htmlFor="name">Your Name <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    value={inquiryForm.name}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                    placeholder="Full name"
                    required
                  />
                </div>
              </div>
              <div className="inquiry-form-row">
                <div className="inquiry-form-group">
                  <label htmlFor="email">Email <span className="required">*</span></label>
                  <input
                    type="email"
                    id="email"
                    value={inquiryForm.email}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div className="inquiry-form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={inquiryForm.phone}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                    placeholder="+65"
                  />
                </div>
              </div>
              <div className="inquiry-form-group">
                <label htmlFor="estimatedQuantity">Estimated Quantity</label>
                <input
                  type="text"
                  id="estimatedQuantity"
                  value={inquiryForm.estimatedQuantity}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, estimatedQuantity: e.target.value })}
                  placeholder="e.g., 500 sets/month"
                />
              </div>
              <div className="inquiry-form-group">
                <label htmlFor="message">Message <span className="required">*</span></label>
                <textarea
                  id="message"
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                  placeholder="Tell us about your requirements..."
                  rows={5}
                  required
                />
              </div>
              <button type="submit" className="btn-submit-inquiry" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModalVisible && (
        <div 
          className="inquiry-modal-overlay" 
          onClick={closeSuccessModal}
        >
          <div className="inquiry-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inquiry-modal-header">
              <div>
                <h2>Send Inquiry</h2>
                {selectedProduct && (
                  <p className="inquiry-product-name">Product: {selectedProduct.name}</p>
                )}
              </div>
              <button 
                className="inquiry-modal-close"
                onClick={closeSuccessModal}
              >
                ×
              </button>
            </div>
            <div className="success-content">
              <div className="success-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="32" fill="#10B981"/>
                  <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="success-title">Inquiry Submitted!</h3>
              <p className="success-message">We'll get back to you shortly.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
